import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
 ChevronDown, 
 ChevronUp, 
 HelpCircle, 
 Shield, 
 Lock, 
 Clock, 
 RefreshCcw,
 CheckCircle,
 Database,
 FileText,
 Eye,
 ShieldCheck
} from 'lucide-react';

const FAQAndSecurity = () => {
 const { t } = useTranslation();
 const [openFAQ, setOpenFAQ] = useState(null);

 const faqs = [
 {
 id: 1,
 emoji: '🔐',
 icon: <Shield className="w-5 h-5" />,
 questionKey: 'faq.questions.legal.question',
 answerKey: 'faq.questions.legal.answer',
 gradient: 'from-green-500 to-green-600'
 },
 {
 id: 2,
 emoji: '🛡️',
 icon: <CheckCircle className="w-5 h-5" />,
 questionKey: 'faq.questions.banned.question',
 answerKey: 'faq.questions.banned.answer',
 gradient: 'from-accent-500 to-accent-400'
 },
 {
 id: 3,
 emoji: '🔒',
 icon: <Lock className="w-5 h-5" />,
 questionKey: 'faq.questions.dataSafe.question',
 answerKey: 'faq.questions.dataSafe.answer',
 gradient: 'from-accent-500 to-accent-600'
 },
 {
 id: 4,
 emoji: '⏱️',
 icon: <Clock className="w-5 h-5" />,
 questionKey: 'faq.questions.howQuickly.question',
 answerKey: 'faq.questions.howQuickly.answer',
 gradient: 'from-accent-500 to-accent-600'
 },
 {
 id: 5,
 emoji: '💰',
 icon: <RefreshCcw className="w-5 h-5" />,
 questionKey: 'faq.questions.notSatisfied.question',
 answerKey: 'faq.questions.notSatisfied.answer',
 gradient: 'from-accent-500 to-accent-400'
 }
 ];

 const securityFeatures = [
 {
 id: 1,
 emoji: '🛡️',
 icon: <Shield className="w-6 h-6" />,
 titleKey: 'securityPrivacy.features.bankLevel.title',
 features: [
 'securityPrivacy.features.bankLevel.feature1',
 'securityPrivacy.features.bankLevel.feature2',
 'securityPrivacy.features.bankLevel.feature3'
 ],
 gradient: 'from-accent-500 to-accent-400'
 },
 {
 id: 2,
 emoji: '🔐',
 icon: <Database className="w-6 h-6" />,
 titleKey: 'securityPrivacy.features.dataIsolation.title',
 features: [
 'securityPrivacy.features.dataIsolation.feature1',
 'securityPrivacy.features.dataIsolation.feature2',
 'securityPrivacy.features.dataIsolation.feature3'
 ],
 gradient: 'from-green-500 to-green-600'
 },
 {
 id: 3,
 emoji: '📋',
 icon: <FileText className="w-6 h-6" />,
 titleKey: 'securityPrivacy.features.privacyCompliance.title',
 features: [
 'securityPrivacy.features.privacyCompliance.feature1',
 'securityPrivacy.features.privacyCompliance.feature2',
 'securityPrivacy.features.privacyCompliance.feature3'
 ],
 gradient: 'from-accent-500 to-accent-600'
 },
 {
 id: 4,
 emoji: '🔍',
 icon: <Eye className="w-6 h-6" />,
 titleKey: 'securityPrivacy.features.monitoring.title',
 features: [
 'securityPrivacy.features.monitoring.feature1',
 'securityPrivacy.features.monitoring.feature2',
 'securityPrivacy.features.monitoring.feature3'
 ],
 gradient: 'from-accent-500 to-accent-600'
 }
 ];

 const toggleFAQ = (id) => {
 setOpenFAQ(openFAQ === id ? null : id);
 };

 return (
 <section className="py-20 lg:py-28 bg-gradient-to-br from-accent-50/30 to-cream-100">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
 
 {/* FAQ 部分 */}
 <div className="mb-20">
 <div className="max-w-4xl mx-auto">
 <div className="text-center mb-12">
 <div className="inline-flex items-center bg-accent-100 text-accent-600 rounded-full px-6 py-3 text-sm font-semibold mb-6">
 <HelpCircle className="w-4 h-4 mr-2" />
 {t('faq.badge')}
 </div>
 <h2 className="text-3xl lg:text-4xl font-bold text-ink-900 mb-6">
 {t('faq.title')}
 </h2>
 <p className="text-lg text-ink-600">
 {t('faq.subtitle')}
 </p>
 </div>

 {/* FAQ Items */}
 <div className="space-y-4">
 {faqs.map((faq) => (
 <div
 key={faq.id}
 className="bg-cream-50 rounded-2xl shadow-lg border border-ink-100 overflow-hidden transition-all duration-300 hover:shadow-xl"
 >
 {/* Question Header */}
 <button
 onClick={() => toggleFAQ(faq.id)}
 className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-cream-100 transition-colors duration-200"
 >
 <div className="flex items-center gap-4">
 <div className={`w-10 h-10 bg-gradient-to-r ${faq.gradient} rounded-xl flex items-center justify-center text-white shadow-lg`}>
 {faq.icon}
 </div>
 <div className="flex items-center gap-3">
 <span className="text-xl">{faq.emoji}</span>
 <h3 className="text-base font-bold text-ink-900">
 {t(faq.questionKey)}
 </h3>
 </div>
 </div>
 <div className="flex-shrink-0">
 {openFAQ === faq.id ? (
 <ChevronUp className="w-5 h-5 text-ink-500" />
 ) : (
 <ChevronDown className="w-5 h-5 text-ink-500" />
 )}
 </div>
 </button>

 {/* Answer Content */}
 {openFAQ === faq.id && (
 <div className="px-6 pb-6">
 <div className="ml-14 pt-2">
 <div className={`h-1 w-12 bg-gradient-to-r ${faq.gradient} rounded-full mb-4`}></div>
 <p className="text-ink-700 leading-relaxed text-sm">
 {t(faq.answerKey)}
 </p>
 </div>
 </div>
 )}
 </div>
 ))}
 </div>

 {/* Bottom CTA */}
 <div className="text-center mt-8">
 <p className="text-ink-600 mb-4 text-sm">
 {t('faq.stillHaveQuestions')}
 </p>
 <button className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-white px-6 py-3 rounded-2xl font-semibold transition-transform duration-300 shadow-lg">
 <HelpCircle className="w-4 h-4" />
 {t('faq.contactSupport')}
 </button>
 </div>
 </div>
 </div>

 {/* Security & Privacy 部分 */}
 <div>
 <div className="max-w-6xl mx-auto">
 <div className="text-center mb-12">
 <div className="inline-flex items-center bg-accent-100 text-accent-600 rounded-full px-6 py-3 text-sm font-semibold mb-6">
 <ShieldCheck className="w-4 h-4 mr-2" />
 {t('securityPrivacy.badge')}
 </div>
 <h2 className="text-3xl lg:text-4xl font-bold text-ink-900 mb-6">
 {t('securityPrivacy.title')}
 </h2>
 <p className="text-lg text-ink-600">
 {t('securityPrivacy.subtitle')}
 </p>
 </div>

 {/* Security Features */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
 {securityFeatures.map((feature) => (
 <div
 key={feature.id}
 className="bg-cream-50 rounded-2xl border border-ink-100 p-6 hover:shadow-lg transition-all duration-300 group"
 >
 {/* Header */}
 <div className="flex items-center gap-4 mb-4">
 <div className={`w-12 h-12 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center text-white shadow-lg transition-transform duration-300`}>
 {feature.icon}
 </div>
 <div className="flex items-center gap-2">
 <span className="text-2xl">{feature.emoji}</span>
 <h3 className="text-lg font-bold text-ink-900">
 {t(feature.titleKey)}
 </h3>
 </div>
 </div>

 {/* Features List */}
 <ul className="space-y-2">
 {feature.features.map((featureItem, index) => (
 <li key={index} className="flex items-start gap-3">
 <div className={`w-5 h-5 bg-gradient-to-r ${feature.gradient} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
 <CheckCircle className="w-3 h-3 text-white" />
 </div>
 <span className="text-ink-700 text-sm">
 {t(featureItem)}
 </span>
 </li>
 ))}
 </ul>
 </div>
 ))}
 </div>

 {/* Trust badges */}
 <div className="mt-8 p-6 bg-cream-50 rounded-2xl border border-ink-100">
 <div className="flex items-center justify-center gap-3 mb-4">
 <Lock className="w-6 h-6 text-accent-500" />
 <h3 className="text-lg font-bold text-ink-900">
 {t('securityPrivacy.assurance.title')}
 </h3>
 </div>
 <p className="text-ink-700 text-center mb-6 text-sm leading-relaxed">
 {t('securityPrivacy.assurance.description')}
 </p>
 
 <div className="flex items-center justify-center gap-6 flex-wrap">
 <div className="flex items-center gap-2 text-green-600">
 <CheckCircle className="w-4 h-4" />
 <span className="font-semibold text-sm">{t('securityPrivacy.badges.certified')}</span>
 </div>
 <div className="flex items-center gap-2 text-accent-600">
 <Shield className="w-4 h-4" />
 <span className="font-semibold text-sm">{t('securityPrivacy.badges.encrypted')}</span>
 </div>
 <div className="flex items-center gap-2 text-accent-600">
 <Eye className="w-4 h-4" />
 <span className="font-semibold text-sm">{t('securityPrivacy.badges.monitored')}</span>
 </div>
 </div>
 </div>
 </div>
 </div>
 </div>
 </section>
 );
};

export default FAQAndSecurity;